import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Check } from 'lucide-react'
import { useT } from '../lib/i18n.tsx'
import { cn } from './util.ts'

export interface EmptyStateProps {
  icon?: LucideIcon
  title: string
  /** One or two short sentences that say what to do next. */
  body?: ReactNode
  action?: ReactNode
  /** First-run steps, e.g. on Today. */
  steps?: { label: string; done?: boolean }[]
  /** 'danger' for a failed load (the error state of a view). */
  tone?: 'neutral' | 'danger'
  /** Heading level of the title: 'h1' when the empty state is the whole screen (not found, offline). */
  heading?: 'h1' | 'h2' | 'h3'
  className?: string
  compact?: boolean
}

/** What an empty (or failed) view shows: <EmptyState icon={Inbox} title="Nothing due" body="…" action={<Button …/>} />. */
export function EmptyState({ icon: Icon, title, body, action, steps, tone = 'neutral', heading: Heading = 'h3', className, compact }: EmptyStateProps) {
  const t = useT()
  return (
    <div
      role={tone === 'danger' ? 'alert' : undefined}
      className={cn('flex flex-col items-center text-center', compact ? 'gap-2 px-4 py-8' : 'gap-3 px-6 py-14', className)}
    >
      {Icon && (
        <span
          className={cn(
            'relative mb-1 inline-flex size-12 items-center justify-center rounded-xl shadow-raised ring-1 ring-inset',
            tone === 'danger' ? 'bg-danger-soft text-danger ring-danger-line' : 'bg-raised text-ink-muted ring-line-strong',
          )}
        >
          {tone === 'neutral' && <span aria-hidden className="absolute inset-0 rounded-xl hatch opacity-60" />}
          <Icon size={20} strokeWidth={1.6} aria-hidden className="relative" />
        </span>
      )}
      <Heading className="font-display text-lg font-semibold text-ink">{title}</Heading>
      {body && <p className="max-w-sm text-base text-ink-muted">{body}</p>}
      {steps && (
        <ol className="mt-2 flex w-full max-w-sm flex-col gap-1.5 text-left">
          {steps.map((s, i) => (
            <li key={s.label} className="flex items-center gap-3 rounded-md bg-raised px-3 py-2 ring-1 ring-line ring-inset">
              <span
                className={cn(
                  'inline-flex size-5 shrink-0 items-center justify-center rounded-full font-mono text-xs',
                  s.done ? 'bg-ok-soft text-ok' : 'bg-hover text-ink-muted',
                )}
              >
                {s.done ? <Check size={12} strokeWidth={2.5} aria-label={t('common.done')} /> : i + 1}
              </span>
              <span className={cn('text-sm', s.done ? 'text-ink-faint line-through' : 'text-ink')}>{s.label}</span>
            </li>
          ))}
        </ol>
      )}
      {action && <div className="mt-2 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  )
}
